import { UserEvents, trackEvent } from "@/analytics";
import { cn, rgbDataURL } from "@/components/common/utils";
import { useEffect, useState } from "react";

import { CollectionRow } from "@/data/v2/models/collections";
import { Comedian } from "@/data/comedians";
import Image from "next/image";
import Link from "next/link";
import { ListVideo } from "lucide-react";
import LockedUnlockedOverlay from "./SpecialCard/LockedUnlockedOverlay";
import { getCDNImageURL } from "@/data/images";
import { useProductsForCollection } from "@punchup/data/hooks";

// Bundle data interfaces
export type BundleWithUnlocked = CollectionRow & {
  unlocked: boolean;
  featured_special?: {
    preview_img?: string | null;
    small_preview_img?: string | null;
  } | null;
  specials_count: number;
  price?: number;
};

export interface BundleCardProps {
  comedian: Comedian;
  bundle: BundleWithUnlocked;
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
  useSquareImgOnMobile: boolean;
  className?: string;
  mobileClassName?: string;
  location: string;
  hideLockOverlay?: boolean;
}

// Video count overlay component
interface VideoCountOverlayProps {
  videoCount: number;
}

const VideoCountOverlay: React.FC<VideoCountOverlayProps> = ({
  videoCount,
}) => {
  return (
    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black bg-opacity-75 rounded px-2 py-1 text-white text-sm font-medium">
      <ListVideo className="w-4 h-4" />
      <span>{videoCount}</span>
    </div>
  );
};

// Bundle image component
interface BundleCardImageProps {
  width: number;
  height: number;
  comedianDisplayName: string | null;
  imgSrc: string | null;
  unlocked: boolean;
  className?: string;
}

const BundleCardImage: React.FC<BundleCardImageProps> = ({
  width,
  height,
  comedianDisplayName,
  imgSrc,
  unlocked,
  className,
}) => {
  if (!imgSrc) return null;

  return (
    <div
      className={cn("relative rounded-xl", className)}
      style={{ width, height }}
    >
      <Image
        src={imgSrc}
        alt={`Promotional image for ${comedianDisplayName}'s collection of videos.`}
        fill
        placeholder="blur"
        blurDataURL={rgbDataURL(16, 16, 16)}
        className={cn(
          "object-cover object-top bg-neutral-300 transition-all lg:group-hover:scale-105 duration-150 group-hover:ease-in-out",
          unlocked ? "" : "grayscale"
        )}
      />
    </div>
  );
};

// Bundle title and video count component
interface BundleTitleAndVideoCountProps {
  title: string;
  videoCount: number;
}

const BundleTitleAndVideoCount: React.FC<BundleTitleAndVideoCountProps> = ({
  title,
  videoCount,
}) => {
  return (
    <>
      <h3 className="font-sans text-xl font-semibold normal-case text-slate-gray-900 md:text-2xl line-clamp-3 group-hover:underline underline-offset-2 decoration-2">
        {title}
      </h3>
      <span className="flex items-center text-sm mt-1.5 text-slate-gray-900 line-clamp-2">
        {videoCount} video{videoCount !== 1 ? "s" : ""}
      </span>
    </>
  );
};

// Bundle shimmer loading component
export const BundleCardShimmer: React.FC<{
  width?: number;
  mobileWidth?: number;
}> = ({ width = 420, mobileWidth = 340 }) => {
  return (
    <div className={`w-[${mobileWidth}px] lg:w-[${width}px]`}>
      <div className="overflow-hidden bg-gray-200 rounded-xl animate-pulse">
        <div className="h-[236px] object-cover hidden lg:block">
          <div className="h-[236px] object-cover lg:block" />
        </div>
        <div className="h-[236px] object-contain bg-grey lg:hidden" />
      </div>
      <div className="py-4 overflow-hidden">
        <div className="rounded-lg normal-case font-sans font-semibold bg-gray-200 animate-pulse h-7 lg:h-9 w-[12rem] lg:w-[24rem] mb-2 lg:mb-3" />
        <div className="flex items-center w-20 h-5 mb-4 text-sm text-white bg-gray-200 rounded-lg animate-pulse lg:w-32" />
      </div>
    </div>
  );
};

export default function BundleCard({
  comedian,
  bundle,
  width,
  height,
  mobileWidth,
  mobileHeight,
  useSquareImgOnMobile,
  className,
  mobileClassName,
  location,
  hideLockOverlay = false,
}: BundleCardProps) {
  const {
    unlocked,
    slug,
    name,
    requires_purchase,
    requires_subscription,
    featured_special,
    price = 0,
    id,
    specials_count,
  } = bundle;
  const [posterImg, setPosterImg] = useState<string | null>(null);
  const [mobilePosterImg, setMobilePosterImg] = useState<string | null>(null);
  const { products } = useProductsForCollection(bundle);

  useEffect(() => {
    const getImgSrc = async () => {
      // Use featured special's preview image or fallback to comedian's about image
      const previewImg = featured_special?.preview_img;
      const smallPreviewImg = featured_special?.small_preview_img;

      const posterImg = previewImg
        ? getCDNImageURL(
            previewImg,
            `gravity=auto,fit=crop,height=${height},width=${width}`
          )
        : comedian.about_img
          ? getCDNImageURL(
              comedian.about_img,
              `gravity=auto,fit=crop,height=${height},width=${width}`
            )
          : null;
      setPosterImg(posterImg);

      const mobilePosterImg = smallPreviewImg
        ? getCDNImageURL(
            smallPreviewImg,
            `gravity=auto,fit=crop,height=${mobileHeight},width=${mobileWidth}`
          )
        : previewImg
          ? getCDNImageURL(
              previewImg,
              `gravity=auto,fit=crop,height=${mobileHeight},width=${mobileWidth}`
            )
          : comedian.about_img
            ? getCDNImageURL(
                comedian.about_img,
                `gravity=auto,fit=crop,height=${mobileHeight},width=${mobileWidth}`
              )
            : null;
      setMobilePosterImg(mobilePosterImg);
    };

    if (!mobilePosterImg && !posterImg) {
      getImgSrc();
    }
  }, [
    mobilePosterImg,
    posterImg,
    featured_special?.preview_img,
    featured_special?.small_preview_img,
    comedian.about_img,
    width,
    height,
    mobileWidth,
    mobileHeight,
  ]);

  if (!mobilePosterImg && !posterImg) return <BundleCardShimmer />;

  const trackClickedWatchBundle = () => {
    trackEvent(UserEvents.CLICKED_WATCH_BUNDLE, {
      BundleId: id,
      ComedianId: comedian.id,
      BundlePrice: price,
      source: location,
      unlocked,
      videoCount: specials_count,
    });
  };

  const handleUnlockedBundleClick = () => {
    trackClickedWatchBundle();

    trackEvent(UserEvents.CLICKED_WATCH_UNLOCKED_BUNDLE, {
      BundleId: id,
      ComedianId: comedian.id,
      BundlePrice: price,
      location,
      videoCount: specials_count,
    });
  };

  const handleLockedBundleClick = () => {
    trackClickedWatchBundle();

    trackEvent(UserEvents.CLICKED_UNLOCK_BUNDLE, {
      BundleId: id,
      ComedianId: comedian.id,
      BundlePrice: price,
      location,
      videoCount: specials_count,
    });
  };

  const handleLinkClick = () =>
    unlocked ? handleUnlockedBundleClick : handleLockedBundleClick;

  // Use the same href for both unlocked and locked bundles
  const cardHref = unlocked
    ? `/${comedian.slug}/bundles/${slug}`
    : `/${comedian.slug}/bundles/${slug}`;

  return (
    <>
      {/* mobile version */}
      <Link
        className={cn("group md:hidden", mobileClassName)}
        href={cardHref}
        onClick={handleLinkClick}
      >
        <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-xl">
          <BundleCardImage
            width={mobileWidth}
            height={mobileHeight}
            comedianDisplayName={comedian.display_name}
            imgSrc={useSquareImgOnMobile ? mobilePosterImg : posterImg}
            className={useSquareImgOnMobile ? "aspect-square" : "aspect-video"}
            unlocked={unlocked}
          />
          {!hideLockOverlay && (
            <LockedUnlockedOverlay
              unlocked={unlocked}
              product={products?.[0] ?? null}
              specialRequiresPurchase={requires_purchase}
              specialRequiresSubscription={requires_subscription}
            />
          )}
          <VideoCountOverlay videoCount={specials_count} />
        </div>
        <div className="flex flex-col justify-center flex-1 w-full pt-3 pr-1 md:pt-4">
          <BundleTitleAndVideoCount title={name} videoCount={specials_count} />
        </div>
      </Link>
      {/* desktop version */}
      <Link
        className={cn("group hidden md:block", className)}
        style={{ width }}
        href={cardHref}
        onClick={handleLinkClick}
      >
        <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-xl">
          <BundleCardImage
            width={width}
            height={height}
            className="aspect-video"
            comedianDisplayName={comedian.display_name}
            imgSrc={posterImg}
            unlocked={unlocked}
          />
          {!hideLockOverlay && (
            <LockedUnlockedOverlay
              unlocked={unlocked}
              product={products?.[0] ?? null}
              specialRequiresPurchase={requires_purchase}
              specialRequiresSubscription={requires_subscription}
            />
          )}
          <VideoCountOverlay videoCount={specials_count} />
        </div>
        <div className="flex flex-col justify-center flex-1 w-full pt-3 pr-1 md:pt-4">
          <BundleTitleAndVideoCount title={name} videoCount={specials_count} />
        </div>
      </Link>
    </>
  );
}
